import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useComboboxInput, useHTMLInputCursorState } from '@platejs/combobox/react';
import { PathApi, TextApi } from 'platejs';
import type { SlateEditor, TElement } from 'platejs';
import { useEditorRef } from 'platejs/react';
import { usePopper } from 'react-popper';
import { useLatest } from 'react-use';

import { getWasTriggerTyped } from './trigger-origin';
import { EDITOR_PICKER_HEIGHT } from '../../AlineMarkdownEditor/utils/styles';

/**
 * A picker key can reach us from the hidden input or from the document-level capture
 * listener, so every handler has to accept both shapes.
 */
export type PickerKeyboardEvent = KeyboardEvent | React.KeyboardEvent<HTMLInputElement>;

/** The keyCode browsers report for every key pressed while an IME is composing. */
const IME_COMPOSING_KEY_CODE = 229;
const PANEL_VIEWPORT_MARGIN = 16;
const PANEL_MIN_HEIGHT = 80;

/** Consume a key completely, including listeners already bound to the same target. */
export function stopPickerKeyboardEvent(event: PickerKeyboardEvent): void {
  event.preventDefault();
  event.stopPropagation();
  getNativeKeyboardEvent(event).stopImmediatePropagation();
}

/**
 * Mouse-down handler for the picker's menu controls. It keeps focus, and the caret, in the
 * search input, and keeps the press from reaching the outside-click close.
 */
export function keepPickerFocus(event: React.MouseEvent): void {
  event.preventDefault();
  event.stopPropagation();
}

/**
 * The text the user has typed after the trigger character.
 *
 * A keystroke can land in either of two places: the hidden `<input>` the picker renders,
 * or the trigger's own Slate node, depending on which won the focus race on mount. Reading
 * the node supplies the initial input value. Once the controlled input changes, it owns
 * the query, including an empty string after deleting the initial characters.
 */
export function getTriggerQuery(element: TElement, trigger: string): string {
  const text = element.children.map(child => (TextApi.isText(child) ? child.text : '')).join('');
  return text.startsWith(trigger) ? text.slice(trigger.length) : text;
}

interface UseTriggerPickerParams {
  /** The trigger's Slate node, used to read the typed query and locate the insert point. */
  element: TElement;
  /**
   * Picker-specific key handling (list navigation, selection). Return `true` once the key
   * has been consumed. Escape, and closing on an empty backspace, are handled here. Read
   * through a ref, so it may be a new function on every render.
   */
  onNavigationKeyDown: (event: PickerKeyboardEvent) => boolean;
  /** The literal character that opened the picker, `@` or `/`. */
  trigger: string;
  /**
   * Called when Backspace or Delete is pressed with an empty search. Return `true` after
   * moving up a level (out of a category or sub-menu) to keep the picker open; return
   * `false` to remove the trigger as usual. Read through a ref, like `onNavigationKeyDown`.
   */
  onEmptyDelete?: () => boolean;
  /**
   * True while a portaled dialog owns the interaction. The picker stays mounted, because it
   * is the Slate trigger node and unmounting it would take the dialog with it, but it stops
   * closing on the clicks and keys that belong to the dialog.
   */
  suspended?: boolean;
}

/**
 * The mechanics shared by every `trigger`-character picker in the chat composer.
 *
 * Both the `@` mention picker and the `/` skill picker are Plate trigger comboboxes: a
 * transient inline node holding a hidden input, with a popper menu anchored to it. Only the
 * data source and the menu's rows differ. Everything else lives here once: the combobox
 * contract, the insert point, the focus dance with Slate, popper placement, document-level
 * key capture and outside-click closing.
 *
 * A picker ends one of two ways. Choosing a row takes the trigger out and leaves the caret
 * where it stood, for the chip to go in (`removeTriggerAndRestoreCaret`). Leaving without a
 * choice, by Escape or a click elsewhere, puts back what was typed as plain text
 * (`dismissAsText`), so a slash that started "/api/v1 is down" is not lost. Backspace on an
 * empty query first steps back out of any open sub-menu (`onEmptyDelete`); at the top level
 * it is the one dismissal that removes the trigger outright.
 */
export function useTriggerPicker({
  element,
  onEmptyDelete,
  onNavigationKeyDown,
  suspended = false,
  trigger,
}: UseTriggerPickerParams) {
  const editor = useEditorRef();
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputQuery, setQuery] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLSpanElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const cursorState = useHTMLInputCursorState(inputRef);
  const query = inputQuery ?? getTriggerQuery(element, trigger);

  /**
   * The handlers below are bound to the document and must stay stable, so they read the
   * current values through refs rather than capturing them in their closures.
   */
  const suspendedRef = useLatest(suspended);
  const onNavigationKeyDownRef = useLatest(onNavigationKeyDown);
  const onEmptyDeleteRef = useLatest(onEmptyDelete);
  const queryRef = useLatest(query);

  /**
   * Set once the picker starts closing, so the focus guard below stops reclaiming focus from
   * the editable the close is deliberately handing it to.
   */
  const cancellingRef = useRef(false);

  // Set once a row was checked while the picker stays open; see `noteChoiceMade`.
  const choiceMadeRef = useRef(false);

  const panelAnchor =
    anchorEl?.closest<HTMLElement>('[data-editor-picker-anchor]') ??
    anchorEl?.closest<HTMLElement>('[data-slate-editor]') ??
    anchorEl;
  const openBelow = Boolean(anchorEl?.closest('[data-picker-placement="bottom"]'));
  const [panelMaxHeight, setPanelMaxHeight] = useState(EDITOR_PICKER_HEIGHT);
  const [panelWidth, setPanelWidth] = useState(360);
  useEffect(() => {
    if (!panelAnchor) return;
    const updatePanelSize = () => {
      const rect = panelAnchor.getBoundingClientRect();
      setPanelWidth(rect.width);
      const spaceBelow = window.innerHeight - rect.bottom - PANEL_VIEWPORT_MARGIN;
      const availableSpace = openBelow
        ? spaceBelow
        : Math.max(rect.top - PANEL_VIEWPORT_MARGIN, spaceBelow);
      setPanelMaxHeight(Math.max(PANEL_MIN_HEIGHT, Math.min(EDITOR_PICKER_HEIGHT, availableSpace)));
    };
    updatePanelSize();

    const observer = new ResizeObserver(updatePanelSize);
    observer.observe(panelAnchor);
    window.addEventListener('resize', updatePanelSize);
    window.addEventListener('scroll', updatePanelSize, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updatePanelSize);
      window.removeEventListener('scroll', updatePanelSize, true);
    };
  }, [openBelow, panelAnchor]);

  const { attributes: popperAttributes, styles: popperStyles } = usePopper(
    panelAnchor,
    popperElement,
    {
      modifiers: [
        { name: 'offset', options: { offset: [0, 4] } },
        { enabled: !openBelow, name: 'flip', options: { fallbackPlacements: ['bottom-start'] } },
        {
          name: 'preventOverflow',
          options: { altAxis: true, padding: 8, rootBoundary: 'viewport', tether: false },
        },
      ],
      placement: openBelow ? 'bottom-start' : 'top-start',
      strategy: 'fixed',
    }
  );

  const {
    props: { onKeyDown: handleComboboxKeyDown },
  } = useComboboxInput({
    cancelInputOnArrowLeftRight: false,
    /**
     * Backspace is handled in `handlePickerKeyDown` below, against live values.
     *
     * The hook decides on `cursorState.atStart`, which `useHTMLInputCursorState` recomputes
     * inside a `setTimeout`, so it trails the keydown that reads it by a macrotask and a
     * render. Two ways that goes wrong: typing again straight after emptying the input can
     * put a Backspace in the window where `atStart` is still stale-true, cancelling the
     * trigger mid-search; and when the focus race leaves the query in the Slate node the
     * hidden input stays empty, making `atStart` permanently true so every Backspace
     * cancels. Reading `inputRef.current.value` and the node text instead never trails.
     */
    cancelInputOnBackspace: false,
    cancelInputOnBlur: false,
    /**
     * Removing the trigger deselects it, and the hook's deselect cancel would then try to
     * remove the same node a second time and crash the editor. Outside clicks close the
     * picker through the mousedown listener below instead.
     */
    cancelInputOnDeselect: false,
    // Escape is handled by the capture listener below so both triggers cancel identically.
    cancelInputOnEscape: false,
    cursorState,
    ref: inputRef,
  });

  /**
   * The combobox hook focuses the input on mount, but at the start of an empty paragraph
   * Slate's selection sync runs afterwards and hands focus back to the editable, so the
   * first search keystrokes would land in the document.
   *
   * That sync has no fixed delay, so waiting a frame for it only wins the race on an
   * unloaded machine: when it lands later, nothing reclaims focus again and the picker sits
   * open with the caret still in the document. Reclaim on the editable's own `focusin`
   * instead, which fires whenever the sync actually happens rather than whenever we guessed
   * it would.
   */
  useEffect(() => {
    const input = inputRef.current;
    const editable = editor.api.toDOMNode(editor);
    if (!input || !editable) return;

    const reclaimFocus = () => {
      /**
       * A close hands focus to the editable deliberately, and a dialog owns it while the
       * picker is suspended. Taking it back in either case would fight the interaction.
       */
      if (cancellingRef.current || suspendedRef.current) return;
      // Focusing the input bubbles `focusin` through the editable it lives in.
      if (document.activeElement === input) return;
      input.focus();
    };

    reclaimFocus();
    editable.addEventListener('focusin', reclaimFocus);

    return () => editable.removeEventListener('focusin', reclaimFocus);
  }, [editor, suspendedRef]);

  /**
   * Slate skips the DOM focus while it already believes it is focused, which is the case
   * while the hidden input, a descendant of the editable, still holds it. The input
   * unmounts once the picker closes, so hand focus to the editable directly or the next
   * keystroke lands on a stale caret.
   */
  const focusEditable = useCallback(() => {
    editor.tf.focus();
    editor.api.toDOMNode(editor)?.focus({ preventScroll: true });
  }, [editor]);

  /** Take the trigger out and leave the caret where it stood, ready for the chosen chip. */
  const removeTriggerAndRestoreCaret = useCallback(() => {
    cancellingRef.current = true;
    removeTriggerNode({ editor, element });
  }, [editor, element]);

  /**
   * Close without choosing a row: what was typed goes back as plain text, with the caret
   * after it. Once a row has been checked, the search was only there to find it, so the
   * picker closes without putting it back.
   */
  const dismissAsText = useCallback(() => {
    cancellingRef.current = true;
    const typedTrigger = getWasTriggerTyped(element) ? trigger : '';
    const literalText = choiceMadeRef.current ? '' : `${typedTrigger}${queryRef.current}`;
    removeTriggerNode({ editor, element, literalText });
  }, [editor, element, queryRef, trigger]);

  /** Record that a row was checked while the picker stays open. */
  const noteChoiceMade = useCallback(() => {
    choiceMadeRef.current = true;
  }, []);

  const handlePickerKeyDown = useCallback(
    (event: PickerKeyboardEvent): boolean => {
      if (suspendedRef.current || getIsComposingKeyEvent(event)) return false;

      if (event.key === 'Escape') {
        stopPickerKeyboardEvent(event);
        dismissAsText();
        focusEditable();
        return true;
      }
      /**
       * Deleting out of an empty trigger removes it. The hook's own backspace cancel is not
       * used: it reads a cursor state that lags a keystroke behind once the last search
       * character is erased, which leaves the picker open. The live input value does not lag.
       */
      const isDeleteKey = event.key === 'Backspace' || event.key === 'Delete';
      if (isDeleteKey && !inputRef.current?.value && !queryRef.current) {
        stopPickerKeyboardEvent(event);
        if (onEmptyDeleteRef.current?.()) return true;
        removeTriggerAndRestoreCaret();
        focusEditable();
        return true;
      }

      return onNavigationKeyDownRef.current(event);
    },
    [
      dismissAsText,
      focusEditable,
      onEmptyDeleteRef,
      onNavigationKeyDownRef,
      queryRef,
      removeTriggerAndRestoreCaret,
      suspendedRef,
    ]
  );

  // The combobox hook builds its handler afresh on every render.
  const handleComboboxKeyDownRef = useLatest(handleComboboxKeyDown);

  /** Key handler for the hidden input, falling through to the combobox hook's own. */
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (handlePickerKeyDown(event)) return;
      handleComboboxKeyDownRef.current(event);
    },
    [handleComboboxKeyDownRef, handlePickerKeyDown]
  );

  /**
   * Also handled at the document level, in the capture phase: Slate can see these keys
   * before the hidden input regains focus, and the picker must respond the same way
   * whichever of its elements holds focus. Keys aimed anywhere else on the page are not
   * the picker's, even while it is open.
   */
  useEffect(() => {
    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      const inMenu = Boolean(popperElement?.contains(target));
      if (!inMenu && !editor.api.toDOMNode(editor)?.contains(target)) return;
      // Menu buttons own Enter, Space and focus navigation. Escape still closes the picker.
      if (inMenu && event.key !== 'Escape') return;
      handlePickerKeyDown(event);
    };

    document.addEventListener('keydown', handleDocumentKeyDown, true);

    return () => document.removeEventListener('keydown', handleDocumentKeyDown, true);
  }, [editor, handlePickerKeyDown, popperElement]);

  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      if (suspendedRef.current) return;
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (anchorEl?.contains(target) || popperElement?.contains(target)) return;
      dismissAsText();
    };

    document.addEventListener('mousedown', handleMouseDown);

    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [anchorEl, dismissAsText, popperElement, suspendedRef]);

  return useMemo(
    () => ({
      anchorEl,
      dismissAsText,
      focusEditable,
      handleKeyDown,
      inputRef,
      noteChoiceMade,
      panelMaxHeight,
      panelWidth,
      popperAttributes,
      popperStyles,
      query,
      removeTriggerAndRestoreCaret,
      setAnchorEl,
      setPopperElement,
      setQuery,
    }),
    [
      anchorEl,
      dismissAsText,
      focusEditable,
      handleKeyDown,
      noteChoiceMade,
      panelMaxHeight,
      panelWidth,
      popperAttributes,
      popperStyles,
      query,
      removeTriggerAndRestoreCaret,
    ]
  );
}

function getNativeKeyboardEvent(event: PickerKeyboardEvent): KeyboardEvent {
  return event instanceof KeyboardEvent ? event : event.nativeEvent;
}

/**
 * True for a key that belongs to an IME composition, such as the Enter that confirms a
 * Japanese or Chinese conversion. The picker must leave those alone: acting on that Enter
 * would pick the highlighted row in the middle of the search.
 */
function getIsComposingKeyEvent(event: PickerKeyboardEvent): boolean {
  const nativeEvent = getNativeKeyboardEvent(event);
  return nativeEvent.isComposing || nativeEvent.keyCode === IME_COMPOSING_KEY_CODE;
}

/**
 * Take the trigger node out of the document and put the caret where it stood, optionally
 * writing `literalText` there first and leaving the caret after it.
 *
 * The caret spot is worked out now, not when the picker opened. Rows checked while the
 * picker stays open insert their chips just before the trigger, so a spot remembered from
 * mount would sit in front of those chips.
 */
function removeTriggerNode({
  editor,
  element,
  literalText = '',
}: {
  editor: SlateEditor;
  element: TElement;
  literalText?: string;
}): void {
  const path = editor.api.findPath(element);
  if (!path) return;

  const parentPath = PathApi.parent(path);
  const previousPath = PathApi.previous(path);
  const pointBefore = previousPath ? editor.api.end(previousPath) : editor.api.start(parentPath);
  const pointRef = pointBefore ? editor.api.pointRef(pointBefore) : null;

  editor.tf.removeNodes({ at: path });

  // With nothing before the trigger, the saved point was inside it and went with it.
  const point = pointRef?.unref() ?? editor.api.start(parentPath);
  if (!point) return;

  // `at` bypasses the trigger combobox, so putting back a lone `/` or `@` cannot reopen it.
  if (literalText) editor.tf.insertText(literalText, { at: point });
  editor.tf.select({ offset: point.offset + literalText.length, path: point.path });
}
